import React, { FC } from 'react';
import './NeedForm.css';
import {
	FaBuilding,
	FaCity,
	FaDollarSign,
	FaHouseUser,
	FaSearchDollar,
	FaSearchLocation,
	FaThumbsUp,
	FaUser,
	RequiredNote,
	SelectField,
	DomainFormProps,
	InputField,
	FaCalendarAlt,
	FaMoneyBillAlt,
	SelectOptionType,
	SoNumber
} from '@/Shared';
import { SingleValue } from 'react-select';
import { NeedFormViewModel } from '../../Logic';


/**
 * =====================================================
 * Component: NeedForm
 * =====================================================
 *
 *
 * Domain-specific form UI for Need.
 * Mounted inside GenericDomainForm.
 *
 * Responsibilities:
 * - Render need fields
 * - Use data from formVM (options, values)
 *
 * No permissions, no loading, no orchestration logic here.
 *
 * Author: Samano CASTRE
 * Date: February 15, 2026
 */
const NeedForm: FC<DomainFormProps> = ({
	domainFormHook,
	formRef,
	formikProps,
}) => {
	const { setFieldValue } = formikProps;
	const entityFormVM = domainFormHook.entityFormVM as NeedFormViewModel;

	/**
	 * =====================================================
	 * Main Form Rendering
	 * =====================================================
	 *
	 * Fieldsets organize the form into logical sections for clarity.
	 */
	return (

		<div className="fieldset-wrapper">

			<fieldset>
				<legend>Besoin</legend>
				<SelectField
					label='Cherche'
					required
					icon={<FaSearchDollar />}
					name='operationType'
					options={entityFormVM.operationOptions}
					scrollParent={formRef.current}
					placeholder='Choisir une opération'
				/>
				<SelectField
					label='Nature du bien'
					required
					icon={<FaBuilding />}
					name='propertyCategory'
					options={entityFormVM.categoryOptions}
					scrollParent={formRef.current}
					placeholder='Choisir une catégorie'
				/>
				<SelectField
					label='Usage'
					icon={<FaBuilding />}
					name='usageType'
					options={entityFormVM.propertyUsageOptions}
					placeholder="Choisir un usage"
					scrollParent={formRef.current}
				/>

				<SelectField
					label='Nbre Chambres'
					icon={<FaHouseUser />}
					name='propertyType'
					options={entityFormVM.typeOptions}
					scrollParent={formRef.current}
					placeholder='Choisir le nombre de chambres'
				/>
				<SelectField
					label='Standing'
					icon={<FaSearchLocation />}
					name='propertyStanding'
					options={entityFormVM.standingOptions}
					required
					scrollParent={formRef.current}
					placeholder='Choisir le standing'
				/>
				<RequiredNote />
			</fieldset>
			<fieldset>
				<legend>Localisation & Date</legend>
				<SelectField
					label='Ville'
					required
					icon={<FaCity />}
					name='cityId'
					options={entityFormVM.cityOptions}
					scrollParent={formRef.current}
					onChange={
						(selectedOption) => {
							const firstValue = entityFormVM.handleCityChange(
								(selectedOption as SingleValue<SelectOptionType>)?.value);

							setFieldValue('sectorId', firstValue);
						}
					}
					placeholder='Choisir la ville du bien'
				/>
				<SelectField
					label='Secteur'
					required
					icon={<FaCity />}
					name='sectorId'
					options={entityFormVM.sectorOptions}
					scrollParent={formRef.current}
					placeholder='Choisir le secteur'
				/>
				<InputField
					label='Date (A partir de)'
					icon={<FaCalendarAlt />}
					name='desiredStartAt'
					type='date'
					autoComplete='date' />
				<RequiredNote />
			</fieldset>
			<fieldset>
				<legend>Budget & Résultat</legend>
				<SelectField
					label='Prospect'
					icon={<FaUser />}
					name='contactId'
					required
					options={entityFormVM.contactOptions}
					scrollParent={formRef.current}
					placeholder='Choisir un prospect'
				/>
				<SelectField
					label='Budget'
					icon={<FaDollarSign />}
					name='priceRange'
					options={entityFormVM.priceRangeOptions}
					scrollParent={formRef.current}
					placeholder='Choisir une fourchette'
					required
				/>
				<InputField
					label='Charges (Montant max. souhaité)'
					icon={<FaMoneyBillAlt />}
					name='maxServiceChargeProvision'
					placeholder='Saisir le montant de charges'
					type='text'
					autoComplete='on'
					onChange={ 
						(e) => setFieldValue(
							'maxServiceChargeProvision', 
							SoNumber.formatAmount(e.target.value)
						)
					}
				/>
				<SelectField
					label='Résultat du besoin'
					icon={<FaThumbsUp />}
					name='status'
					required
					options={entityFormVM.statusOptions}
					scrollParent={formRef.current}
					placeholder='Choisir le résultat'
				/>
				<RequiredNote />
			</fieldset>
		</div>
	);
}

export default NeedForm;